import {useMemo} from 'react';

import {
  RegressionStepDataName,
  SINGLE_CHART_OUTPUT_STEP_DATA_NAME,
  MULTIPLE_CHARTS_OUTPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {
  getIconAndTextForChartViewStep,
  getIconAndTextForRegressionStep,
} from 'analysis-tool/charting/AnalysisChartingUtils';
import {getOutputStepDataForChart} from 'analysis-tool/data/getOutputStepDataForDataFrame';
import getLegacyStepFromAnalysis, {getStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import {TOP_LEVEL_ANALYSIS_VIEW_STEP_TYPES} from 'analysis-tool/data-view/AnalysisViewUtils';
import {AnalysisMainViewItemType} from 'analysis-tool/ui/main-view/MainViewTypes';
import getStepSidebarItemDatas from 'analysis-tool/ui/sidebar/legacy-sidebar/getStepSidebarItemDatas';
import {getDatasetSectionSidebarItemDatas, SidebarItemData} from 'analysis-tool/ui/sidebar/SidebarItemDatas';
import {assert, assertAndContinue} from 'Asserts';
import {ResultsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {
  DataFrameDomainNode,
  PipelineStepDataKind,
  PipelineStepStatusCode,
  PipelineStepType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {
  getAllOutputStepDataRawDatas,
  getPipelineStepRawDatasOfKind,
  pipelineStepRawDataIsOfKind,
} from 'pipelines/StepDataUtils';
import {getParentSteps, getTopLevelTableStartStepForViewLevelStep} from 'pipelines/StepLineageUtils';
import {BenchlingDuotoneIconName} from 'ui-library/icons/BenchlingDuotoneIcon';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import pick from 'util/pick';

import {isSendToDocumentEnabled} from './OutputsUtil';
import {
  AnalysisOutputConfigWithoutResultsMapping,
  AnalysisOutputOptionItem,
  AnalysisOutputType,
  OUTPUT_NAME_SEPARATOR,
} from './Types';

function getDatasetSectionTopLevelCharts(pipeline: PipelineGraphQL): Array<SidebarItemData> {
  const allStepsWithChartOutputs = pipeline.execution.steps.filter((step) =>
    step.outputStepDatas.some(
      (stepData) => stepData.kind === PipelineStepDataKind.CHART && stepData.charts.length > 0
    )
  );
  const topLevelChartSteps = allStepsWithChartOutputs.filter((step) =>
    TOP_LEVEL_ANALYSIS_VIEW_STEP_TYPES.includes(step.definition.stepType)
  );
  // Generate sidebar items for these additional chart steps
  const additionalChartSidebarItems = topLevelChartSteps.flatMap((step) =>
    getStepSidebarItemDatas(step)
      .filter((item) => item.mainViewItem.type === AnalysisMainViewItemType.CHART)
      .map((item) => {
        // Rename text to title to match the sidebar item data type
        const {text, ...rest} = item;
        return {
          title: text,
          ...rest,
        };
      })
  );

  return additionalChartSidebarItems;
}

function isResultOptionEnabled(): boolean {
  return isFeatureEnabled({feature: ResultsFeature.RESULTS_CREATE});
}

function getResultOptionIfEnabled(
  baseConfig: Pick<AnalysisOutputConfigWithoutResultsMapping, 'outputName' | 'parentOutputName' | 'step'>,
  dataFrame: DataFrameDomainNode
): Array<AnalysisOutputConfigWithoutResultsMapping> {
  return isResultOptionEnabled()
    ? [
        {
          ...baseConfig,
          dataFrame,
          type: AnalysisOutputType.RESULT as const,
          icon: 'data_frame',
          targetSpecificMappingState: null,
        },
      ]
    : [];
}

function flattenSidebarItemDatas(
  sidebarItems: Array<SidebarItemData>,
  pipeline: PipelineGraphQL,
  indentLevel: number
): Array<AnalysisOutputOptionItem> {
  return sidebarItems.flatMap((sidebarItem) =>
    [...sidebarItemToAnalysisOutputOptions(sidebarItem, pipeline, indentLevel)].concat(
      flattenSidebarItemDatas(sidebarItem.childSidebarItems ?? [], pipeline, indentLevel + 1)
    )
  );
}

function sidebarItemToAnalysisOutputOptions(
  item: SidebarItemData,
  pipeline: PipelineGraphQL,
  indentLevel: number
): Array<AnalysisOutputOptionItem> {
  const stepId = (item.mainViewItem as {stepId: string}).stepId;
  const step = getLegacyStepFromAnalysis(pipeline, stepId);
  const domainStep = getStepFromAnalysis(pipeline, stepId);
  const baseOptionWithNoOutputConfig: AnalysisOutputOptionItem = {
    indentLevel,
    sidebarItemData: pick(item, 'title', 'icon'),
    selectorIndex: -1,
    outputConfigs: [],
    disabledReason: null,
  };

  if (
    [
      AnalysisMainViewItemType.FAILED,
      AnalysisMainViewItemType.RUNNING,
      // EDIT_CHART is sometimes shown when we have an "Unfinished view...", but this doesn't have outputs.
      AnalysisMainViewItemType.EDIT_CHART,
    ].includes(item.mainViewItem.type) ||
    /**
     * The step may be running, even if the view type is not `AnalysisMainViewItemType.RUNNING`.
     *
     * `AnalysisMainViewItemType.DATA_FRAME` is sometimes shown for tables that are running, so the user can
     * see a prior version of data beneath the loading overlay. See `getDatasetRunningSidebarItemData`.
     */
    step.statusCode !== PipelineStepStatusCode.SUCCEEDED
  ) {
    return [baseOptionWithNoOutputConfig];
  }

  switch (item.mainViewItem.type) {
    case AnalysisMainViewItemType.CHART: {
      if (step.statusCode !== PipelineStepStatusCode.SUCCEEDED) {
        return [baseOptionWithNoOutputConfig];
      }
      const isUdfChart = step.definition.stepType === PipelineStepType.USER_DEFINED_FUNCTION;

      let chartStepData;
      let chart;
      let dataFrameStepData;
      let dataFrame: DataFrameDomainNode | null = null;

      if (isUdfChart) {
        chartStepData = domainStep.outputStepDatas.find(
          ({name}) => name === MULTIPLE_CHARTS_OUTPUT_STEP_DATA_NAME
        );
        if (item.mainViewItem.type === AnalysisMainViewItemType.CHART && item.mainViewItem.chartId) {
          const chartId = item.mainViewItem.chartId;
          const outputStepData = getOutputStepDataForChart(step, chartId);
          if (outputStepData) {
            chart = outputStepData.charts.find(
              (outputChart) => !isInaccessible(outputChart) && outputChart.id === chartId
            );
          }
        }
        dataFrame = null;
      } else {
        chartStepData = step.outputStepDatas.find(({name}) => name === SINGLE_CHART_OUTPUT_STEP_DATA_NAME);
        chart = chartStepData?.charts[0];

        dataFrameStepData = domainStep.outputStepDatas.find(
          ({name}) => name === SINGLE_DATASET_OUTPUT_STEP_DATA_NAME
        );
        if (
          dataFrameStepData != null &&
          dataFrameStepData.rawDatas.length > 0 &&
          pipelineStepRawDataIsOfKind(PipelineStepDataKind.DATASET, dataFrameStepData.rawDatas[0])
        ) {
          dataFrame = dataFrameStepData.rawDatas[0];
        }
      }

      if (!isUdfChart) {
        assert(dataFrame != null, 'Data frame should not be null');
      }

      const baseOutputConfig = {
        outputName: String(item.title),
        parentOutputName:
          getTopLevelTableStartStepForViewLevelStep(pipeline, step)?.outputStepDatas[0].dataFrames[0].name ??
          null,
        step,
      };

      const outputConfigs = [];

      // Add chart output if available
      if (chart != null && !isInaccessible(chart) && isSendToDocumentEnabled()) {
        let chartIcon: BenchlingDuotoneIconName | BenchlingIconName;
        if (step.definition.stepType === PipelineStepType.CHART_VIEW) {
          chartIcon = getIconAndTextForChartViewStep(step).icon;
        } else {
          chartIcon = 'scatter-plot' as const;
        }

        outputConfigs.push({
          ...baseOutputConfig,
          type: AnalysisOutputType.CHART as const,
          chart,
          icon: chartIcon,
        });
      }

      if (dataFrame != null) {
        outputConfigs.push({
          ...baseOutputConfig,
          type: AnalysisOutputType.DATASET as const,
          dataFrame,
          icon: 'dataset' as const,
        });

        outputConfigs.push(...getResultOptionIfEnabled(baseOutputConfig, dataFrame));
      }

      return [
        {
          ...baseOptionWithNoOutputConfig,
          outputConfigs,
        },
      ];
    }
    case AnalysisMainViewItemType.DATA_FRAME: {
      const dataFrameId = item.mainViewItem.dataFrameId;
      const optionDataFrame = getAllOutputStepDataRawDatas(domainStep, PipelineStepDataKind.DATASET).find(
        (rawData) => rawData.id === dataFrameId
      );
      if (optionDataFrame == null) {
        return [{...baseOptionWithNoOutputConfig}];
      }

      const baseOutputConfig = {
        outputName: String(item.title),
        parentOutputName:
          getTopLevelTableStartStepForViewLevelStep(pipeline, step)?.outputStepDatas[0].dataFrames[0].name ??
          null,
      };
      return [
        {
          ...baseOptionWithNoOutputConfig,
          outputConfigs: [
            {
              ...baseOutputConfig,
              type: AnalysisOutputType.DATASET as const,
              dataFrame: optionDataFrame,
              step,
              icon: 'dataset' as const,
            },
            ...getResultOptionIfEnabled({...baseOutputConfig, step}, optionDataFrame),
          ],
        },
      ];
    }
    case AnalysisMainViewItemType.REGRESSION: {
      const optionDataFrame = getPipelineStepRawDatasOfKind(
        PipelineStepDataKind.DATASET,
        domainStep.inputStepDatas[0]
      )[0];
      const modelOutputDataFrame = getPipelineStepRawDatasOfKind(
        PipelineStepDataKind.DATASET,
        domainStep.outputStepDatas.find((stepData) => stepData.name === RegressionStepDataName.MODEL_OUTPUT)!
      )[0];
      const modelVariablesDataFrame = getPipelineStepRawDatasOfKind(
        PipelineStepDataKind.DATASET,
        domainStep.outputStepDatas.find(
          (stepData) => stepData.name === RegressionStepDataName.MODEL_VARIABLES
        )!
      )[0];
      // We need the regression's parent step because we need the parent data frame to be an output data frame
      const [parentStep] = getParentSteps(pipeline, step);

      const baseOutputConfig = {
        dataFrame: optionDataFrame,
        step,
        outputName: String(item.title),
        parentOutputName:
          getTopLevelTableStartStepForViewLevelStep(pipeline, step)?.outputStepDatas[0].dataFrames[0].name ??
          null,
      };
      const modelOutputBaseOutputConfig = {
        dataFrame: modelOutputDataFrame,
        step,
        outputName: `${item.title}${OUTPUT_NAME_SEPARATOR}${RegressionStepDataName.MODEL_OUTPUT}`,
        parentOutputName:
          getTopLevelTableStartStepForViewLevelStep(pipeline, step)?.outputStepDatas[0].dataFrames[0].name ??
          null,
      };

      const modelVariableBaseOutputConfig = {
        dataFrame: modelVariablesDataFrame,
        step,
        outputName: `${item.title}${OUTPUT_NAME_SEPARATOR}${RegressionStepDataName.MODEL_VARIABLES}`,
        parentOutputName:
          getTopLevelTableStartStepForViewLevelStep(pipeline, step)?.outputStepDatas[0].dataFrames[0].name ??
          null,
      };

      const chartStepData = step.outputStepDatas.find(
        ({name}) => name === SINGLE_CHART_OUTPUT_STEP_DATA_NAME
      );
      const chart = chartStepData?.charts[0];

      return [
        {
          ...baseOptionWithNoOutputConfig,
          outputConfigs: [
            ...(chart != null && !isInaccessible(chart) && isSendToDocumentEnabled()
              ? [
                  {
                    ...baseOutputConfig,
                    type: AnalysisOutputType.CHART as const,
                    chart,
                    icon: getIconAndTextForRegressionStep(step).icon,
                  },
                ]
              : []),
            {
              ...baseOutputConfig,
              step: parentStep,
              type: AnalysisOutputType.DATASET as const,
              icon: 'dataset' as const,
            },
            ...getResultOptionIfEnabled({...baseOutputConfig, step: parentStep}, baseOutputConfig.dataFrame),
          ],
        },
        modelOutputDataFrame != null
          ? {
              ...baseOptionWithNoOutputConfig,
              outputConfigs: [
                {
                  ...modelOutputBaseOutputConfig,
                  type: AnalysisOutputType.DATASET as const,
                  icon: 'dataset' as const,
                },
                ...getResultOptionIfEnabled(
                  modelOutputBaseOutputConfig,
                  modelOutputBaseOutputConfig.dataFrame
                ),
              ],
            }
          : baseOptionWithNoOutputConfig,
        modelVariablesDataFrame != null
          ? {
              ...baseOptionWithNoOutputConfig,
              outputConfigs: [
                {
                  ...modelVariableBaseOutputConfig,
                  type: AnalysisOutputType.DATASET as const,
                  icon: 'dataset' as const,
                },
                ...getResultOptionIfEnabled(
                  modelVariableBaseOutputConfig,
                  modelVariableBaseOutputConfig.dataFrame
                ),
              ],
            }
          : baseOptionWithNoOutputConfig,
      ];
    }

    default:
      assertAndContinue(false, 'AnalysisOutputSelector received unexpected item type', {
        itemType: item.mainViewItem.type,
      });
      return [baseOptionWithNoOutputConfig];
  }
}

/**
 * @deprecated TODO(BNCH-137631): Remove deprecated views logic
 */
export default function usePossibleOutputOptions(pipeline: PipelineGraphQL): Array<AnalysisOutputOptionItem> {
  return useMemo(() => {
    const datasetSidebarItems = getDatasetSectionSidebarItemDatas(pipeline);
    const additionalChartSidebarItems = getDatasetSectionTopLevelCharts(pipeline);

    return flattenSidebarItemDatas([...datasetSidebarItems, ...additionalChartSidebarItems], pipeline, 0).map(
      (option, index) => ({
        ...option,
        selectorIndex: index,
      })
    );
  }, [pipeline]);
}
